import { PvpFlag, TICK_MS, flagStateOf } from "odyssey-shared";
import type { FlagState } from "../../ui/kit/FlagMark.tsx";
import { lowHealthLit } from "../../ui/kit/health.ts";

/**
 * The player card's flag mark over the end of its Combatant window (ADR-073's amendment of 2026-09-27, item 9): from
 * `FLICKER_MS` before the end the Combatant mark flickers, from `ALTERNATE_MS` before it alternates with the mark of
 * the state the window ends in, and past the end it keeps alternating until the snapshot's byte leaves Combatant.
 * The same countdown runs over a flag switch while the byte carries the switching bit (ADR-076's amendment of
 * 2026-09-28): a switch toward Protected flickers and then alternates on the same timings, one toward Flag on
 * alternates throughout, between the Protected and Flagged marks. Alternating shows exactly one mark at any moment,
 * the current one in the lit half of each beat and the next in the dark half. The rules live here apart from the
 * component, so they are tested without React; the effects run on the low-health flash's period and phase
 * (`LOW_FLASH_MS`, `holdFlashPhase`, `lowHealthLit`).
 */

/**
 * How long before the window's end the Combatant mark starts to flicker.
 */
export const FLICKER_MS = 10_000;

/**
 * How long before the window's end the mark starts to alternate with the state it ends in.
 */
export const ALTERNATE_MS = 5_000;

/**
 * What the mark does now: holds, flickers, or alternates with the mark it becomes.
 */
export type CountdownPhase = "steady" | "flicker" | "alternate";

/**
 * The mark's countdown at a moment: its phase, the state and lock the window ends in, and how many milliseconds until
 * the phase next changes (null when it will not change before the flag byte does).
 */
export interface FlagCountdown {
  readonly phase: CountdownPhase;
  readonly next: { readonly state: FlagState; readonly locked: boolean };
  readonly changesInMs: number | null;
}

/**
 * When the player's Combatant window ends on the page clock, from a `pvpState`: the receipt time plus the ticks left.
 *
 * @param nowMs The page clock at receipt (`performance.now()`).
 * @param combatantTicks The ticks left in the window, 0 for none.
 * @returns The end on the page clock, or null when there is no window.
 * @example
 * combatantEndsAt(1000, 600);
 * // 31000
 */
export function combatantEndsAt(nowMs: number, combatantTicks: number): number | null {
  return combatantTicks > 0 ? nowMs + combatantTicks * TICK_MS : null;
}

/**
 * The state a Combatant window ends in: zone-locked Flagged when the player's own byte carries a Hostile area's lock,
 * Flagged with Flag on, otherwise Protected (ADR-073's amendment, item 3).
 *
 * @param flag The player's own flag byte.
 * @param preference The player's PvP preference, `PvpFlag.Protected` or `PvpFlag.Flagged`.
 * @returns The state and whether an area holds it.
 */
export function endsIn(flag: number, preference: number): { readonly state: FlagState; readonly locked: boolean } {
  if ((flag & PvpFlag.ZoneLocked) !== 0) {
    return { state: "flagged", locked: true };
  }
  return { state: preference === PvpFlag.Flagged ? "flagged" : "protected", locked: false };
}

/**
 * The player's flag switch as the countdown reads it: when it ends on the page clock (null when none counts) and the
 * preference it counts toward.
 */
export interface CountdownSwitch {
  readonly endsAt: number | null;
  readonly toward: number;
}

/**
 * The flag byte a switching byte blinks toward (ADR-076's amendment of 2026-09-28): the other of Protected and Flagged,
 * with no switching bit and the byte's zone lock, which only a safe passage into a Hostile zone carries with the bit
 * (ADR-076's amendment of 2026-10-03), so both halves of its beat draw the locked marks.
 *
 * @param flag The flag byte, with the switching bit.
 * @returns The byte of the mark the switch ends in.
 * @example
 * switchTarget(PvpFlag.Flagged | PvpFlag.Switching);
 * // 0: Protected
 */
export function switchTarget(flag: number): number {
  return (flag & PvpFlag.ZoneLocked) | (flagStateOf(flag) === PvpFlag.Flagged ? PvpFlag.Protected : PvpFlag.Flagged);
}

/**
 * The flag byte a mark shows at a moment of the clock while it alternates: the byte itself in the lit half of each
 * beat and the switch's target in the dark half, so exactly one mark shows at every moment (`lowHealthLit`). A byte
 * without the switching bit, or any byte under reduced motion, shows as it is.
 *
 * @param flag The flag byte.
 * @param timeMs The clock in ms.
 * @param reducedMotion Whether the viewer asks for reduced motion.
 * @returns The byte whose mark to draw.
 */
export function blinkingFlag(flag: number, timeMs: number, reducedMotion: boolean): number {
  if ((flag & PvpFlag.Switching) === 0 || reducedMotion || lowHealthLit(timeMs)) {
    return flag;
  }
  return switchTarget(flag);
}

/**
 * The phase of a countdown toward an end: steady until `FLICKER_MS` before it, flickering until `ALTERNATE_MS` before
 * it, then alternating, past the end too.
 *
 * @param left Milliseconds until the end.
 * @param next The state it ends in.
 * @returns The countdown.
 */
function towardEnd(left: number, next: FlagCountdown["next"]): FlagCountdown {
  if (left > FLICKER_MS) {
    return { phase: "steady", next, changesInMs: left - FLICKER_MS };
  }
  if (left > ALTERNATE_MS) {
    return { phase: "flicker", next, changesInMs: left - ALTERNATE_MS };
  }
  return { phase: "alternate", next, changesInMs: null };
}

/**
 * The mark's countdown at a moment. A Combatant byte with a known end counts its window down. A byte with the
 * switching bit counts its switch down: toward Protected with a known end on the Combatant timings (the server sets the
 * bit `FLICKER_MS` before it), otherwise alternating. Anything else is steady.
 *
 * @param nowMs The page clock.
 * @param endsAtMs When the window ends on the page clock, or null for no window.
 * @param flag The player's own flag byte.
 * @param preference The player's PvP preference.
 * @param pvpSwitch The player's flag switch, or null when the page knows none.
 * @returns The phase, the state it ends in and when the phase next changes.
 * @example
 * flagCountdown(24_000, 31_000, PvpFlag.Combatant, PvpFlag.Protected);
 * // { phase: "flicker", next: { state: "protected", locked: false }, changesInMs: 2000 }
 */
export function flagCountdown(nowMs: number, endsAtMs: number | null, flag: number, preference: number, pvpSwitch: CountdownSwitch | null = null): FlagCountdown {
  if ((flag & PvpFlag.Switching) !== 0 && flagStateOf(flag) !== PvpFlag.Combatant) {
    const next = { state: flagStateOf(switchTarget(flag)) === PvpFlag.Flagged ? "flagged" : "protected", locked: (flag & PvpFlag.ZoneLocked) !== 0 } as const;
    if (pvpSwitch !== null && pvpSwitch.endsAt !== null && pvpSwitch.toward === PvpFlag.Protected) {
      return towardEnd(pvpSwitch.endsAt - nowMs, next);
    }
    return { phase: "alternate", next, changesInMs: null };
  }
  const next = endsIn(flag, preference);
  if (endsAtMs === null || flagStateOf(flag) !== PvpFlag.Combatant) {
    return { phase: "steady", next, changesInMs: null };
  }
  return towardEnd(endsAtMs - nowMs, next);
}
